import { useEffect, useMemo } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Slider } from "@/components/ui/slider";
import { Reveal, SectionHeading, SectionCta } from "./Reveal";

const AnimatedMoney = ({ value, testid }) => {
  const mv = useMotionValue(0);
  const spring = useSpring(mv, { stiffness: 55, damping: 18 });
  const display = useTransform(spring, (v) => "$" + Math.round(v).toLocaleString());
  useEffect(() => {
    mv.set(value);
  }, [value, mv]);
  return <motion.span data-testid={testid}>{display}</motion.span>;
};

const Field = ({ label, value, display, min, max, step, onChange, testid }) => (
  <div>
    <div className="flex items-baseline justify-between mb-4">
      <label className="text-sm tracking-wide text-zinc-500">{label}</label>
      <span className="font-mono text-lg text-pearl" data-testid={`${testid}-value`}>
        {display}
      </span>
    </div>
    <Slider
      value={[value]}
      onValueChange={([v]) => onChange(v)}
      min={min}
      max={max}
      step={step}
      className="gold-slider"
      data-testid={testid}
      aria-label={label}
    />
  </div>
);

export const Calculator = ({ inputs, setInputs }) => {
  const monthly = useMemo(
    () => inputs.leads * (inputs.lostPct / 100) * (inputs.closeRate / 100) * inputs.caseValue,
    [inputs]
  );
  const annual = monthly * 12;

  return (
    <section id="calculator" data-testid="calculator-section" className="py-24 md:py-36">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <SectionHeading
          eyebrow="The leak ledger"
          title={
            <>
              Most owners know they are losing consults.{" "}
              <span className="italic text-champagne">Almost none know the number.</span>
            </>
          }
          copy="Move the sliders to your practice. On the audit call we replace every estimate here with your real inquiry reports, call logs and pipeline."
        />

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <Reveal className="lg:col-span-7 border border-black/10 bg-coal p-8 md:p-12 space-y-10 shadow-sm">
            <Field
              label="Inbound inquiries per month"
              value={inputs.leads}
              display={inputs.leads}
              min={20}
              max={400}
              step={5}
              onChange={(v) => setInputs({ ...inputs, leads: v })}
              testid="slider-leads"
            />
            <Field
              label="Average case value"
              value={inputs.caseValue}
              display={"$" + inputs.caseValue.toLocaleString()}
              min={500}
              max={25000}
              step={250}
              onChange={(v) => setInputs({ ...inputs, caseValue: v })}
              testid="slider-case-value"
            />
            <Field
              label="Inquiries lost to slow or missed response"
              value={inputs.lostPct}
              display={inputs.lostPct + "%"}
              min={5}
              max={60}
              step={1}
              onChange={(v) => setInputs({ ...inputs, lostPct: v })}
              testid="slider-lost-pct"
            />
            <Field
              label="Consult-to-case close rate"
              value={inputs.closeRate}
              display={inputs.closeRate + "%"}
              min={10}
              max={70}
              step={1}
              onChange={(v) => setInputs({ ...inputs, closeRate: v })}
              testid="slider-close-rate"
            />
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-5 flex flex-col">
            <div className="flex-1 border border-champagne/40 bg-gradient-to-b from-champagne/10 to-white p-8 md:p-12 flex flex-col justify-between shadow-[0_20px_60px_rgba(61,165,221,0.12)]">
              <div>
                <p className="text-sm text-zinc-500">Walking out the door each month</p>
                <p className="mt-3 font-display text-5xl md:text-6xl text-champagne drop-shadow-[0_0_28px_rgba(61,165,221,0.3)]">
                  <AnimatedMoney value={monthly} testid="monthly-loss-value" />
                </p>
                <p className="mt-2 text-xs text-zinc-500">
                  Cases never booked because no one owned the handoff.
                </p>
              </div>
              <div className="mt-12 border-t border-black/10 pt-8">
                <p className="text-sm text-zinc-500">Over twelve months</p>
                <p className="mt-3 font-display text-4xl md:text-5xl text-pearl">
                  <AnimatedMoney value={annual} testid="annual-loss-value" />
                </p>
                <p className="mt-2 text-xs text-zinc-500">
                  You already paid to generate every one of them.
                </p>
              </div>
            </div>
          </Reveal>
        </div>

        <p className="mt-8 text-[11px] text-zinc-400 leading-relaxed max-w-2xl">
          Estimate only, built from your inputs. Your full leak report is calculated from your real
          numbers on the audit call.
        </p>
        <SectionCta id="calculator" label="Get my full leak report" />
      </div>
    </section>
  );
};
